import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BuddyLogo } from "../components/Brand";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { useAuth, startGoogleLogin } from "../lib/auth";
import { homeForRole } from "../lib/roles";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const nav = useNavigate();
  const ROLES = [
    { key: "student", title: t("roles.student"), desc: t("register.studentDesc") },
    { key: "parent", title: t("roles.parent"), desc: t("register.parentDesc") },
    { key: "teacher", title: t("roles.teacher"), desc: t("register.teacherDesc") },
    { key: "school_admin", title: t("roles.school_admin"), desc: t("register.schoolAdminDesc") },
  ];
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", role: "student",
    parent_code: "", class_code: "", school_name: "",
  });
  const [busy, setBusy] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      toast.success(`Welcome, ${u.full_name}! 🎉`);
      if (u.role === "student") nav("/app/student/onboarding");
      else nav(homeForRole(u.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen student-bg flex flex-col">
      <header className="p-6"><Link to="/"><BuddyLogo /></Link></header>
      <main className="flex-1 max-w-[900px] mx-auto w-full px-6 py-6">
        <div className="hb-card p-8 hb-chunky-shadow">
          <h1 className="font-adult font-bold text-3xl text-slate-900">Create your account</h1>
          <p className="text-sm text-slate-600 mt-1">Choose your role to get started.</p>

          <form onSubmit={submit} className="mt-6 space-y-6">
            <div>
              <Label className="mb-2 block">I am a…</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {ROLES.map((r) => (
                  <button
                    type="button"
                    key={r.key}
                    data-testid={`role-${r.key}`}
                    onClick={() => set("role", r.key)}
                    className={`p-4 rounded-xl border text-left transition-colors ${
                      form.role === r.key
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="font-semibold text-slate-900 text-sm">{r.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full name or display name</Label>
                <Input id="full_name" required data-testid="reg-full-name"
                       value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required data-testid="reg-email"
                       value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="password">Password (6+ chars)</Label>
                <Input id="password" type="password" required minLength={6} data-testid="reg-password"
                       value={form.password} onChange={(e) => set("password", e.target.value)} />
              </div>
              {form.role === "student" && (
                <>
                  <div>
                    <Label htmlFor="parent_code">Parent / guardian code (optional)</Label>
                    <Input id="parent_code" data-testid="reg-parent-code" placeholder="e.g. P-XXXXXXXX"
                           value={form.parent_code} onChange={(e) => set("parent_code", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="class_code">Class code (optional)</Label>
                    <Input id="class_code" data-testid="reg-class-code" placeholder="e.g. MATH3-DEMO"
                           value={form.class_code} onChange={(e) => set("class_code", e.target.value)} />
                  </div>
                </>
              )}
              {(form.role === "teacher" || form.role === "school_admin") && (
                <div className="sm:col-span-2">
                  <Label htmlFor="school_name">School name</Label>
                  <Input id="school_name" data-testid="reg-school-name"
                         value={form.school_name} onChange={(e) => set("school_name", e.target.value)} />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500 max-w-md">
                By continuing you agree to our{" "}
                <Link to="/legal/terms" className="underline">Terms</Link>,{" "}
                <Link to="/legal/privacy" className="underline">Privacy Policy</Link>,{" "}
                and, for children, our <Link to="/legal/parent-consent" className="underline">Parent Consent</Link>.
              </div>
              <Button type="submit" disabled={busy} data-testid="reg-submit"
                      className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 py-6">
                {busy ? "Creating…" : "Create account"}
              </Button>
            </div>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">or</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>
          <button
            type="button"
            onClick={startGoogleLogin}
            data-testid="reg-google"
            className="mt-3 w-full flex items-center justify-center gap-3 border border-slate-300 rounded-full py-3
                       bg-white hover:bg-slate-50 text-slate-800 font-medium transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.62l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google (creates a Parent account)
          </button>

          <div className="text-xs mt-6 text-slate-500 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-600 font-medium">Sign in</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
